import { useState } from "react";
import { DollarSign, Activity, Layers, Wifi, WifiOff, ListOrdered, Scale, TrendingDown } from "lucide-react";
import { useDashboardStats, useEAs, useGroups, REFRESH_INTERVAL_MS } from "../hooks/useDashboardData";
import { StatCard } from "../components/StatCard";
import { CardSkeleton } from "../components/Skeleton";
import { StatusBadge } from "../components/StatusBadge";
import { GroupFilterBar } from "../components/GroupFilterBar";
import { RefreshCountdown } from "../components/RefreshCountdown";
import { formatCurrency, formatNumber, relativeTime } from "../lib/format";
import { filterEasByGroup } from "../lib/groups";
import { Link } from "react-router-dom";

export function Dashboard() {
  const [groupId, setGroupId] = useState<number | null>(null);
  const {
    data: stats,
    isLoading: statsLoading,
    isFetching: statsFetching,
    dataUpdatedAt,
  } = useDashboardStats(groupId);
  const { data: easData, isLoading: easLoading } = useEAs();
  const { data: groupsData } = useGroups();

  const groups = groupsData?.groups ?? [];
  const eas = filterEasByGroup(easData?.eas ?? [], groupId, groups);
  const recentEas = [...eas]
    .sort((a, b) => (a.last_heartbeat ?? "").localeCompare(b.last_heartbeat ?? ""))
    .reverse();
  const worstFloatEa = eas.length
    ? eas.reduce((worst, ea) => (ea.daily_max_float < worst.daily_max_float ? ea : worst))
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <GroupFilterBar
          groups={groups}
          value={groupId}
          onChange={setGroupId}
          totalCount={easData ? filterEasByGroup(easData.eas, null, groups).length : undefined}
        />
        <RefreshCountdown
          dataUpdatedAt={dataUpdatedAt}
          intervalMs={REFRESH_INTERVAL_MS}
          isFetching={statsFetching}
          className="font-bold"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-8">
        {statsLoading || !stats ? (
          Array.from({ length: 8 }).map((_, i) => <CardSkeleton key={i} />)
        ) : (
          <>
            <StatCard
              label="Total Profit Today"
              value={formatCurrency(stats.totalProfitToday)}
              icon={<DollarSign className="h-4 w-4" />}
              tone={stats.totalProfitToday >= 0 ? "positive" : "negative"}
            />
            <StatCard
              label="Floating"
              value={formatCurrency(stats.floatingProfit)}
              icon={<Activity className="h-4 w-4" />}
              tone={stats.floatingProfit >= 0 ? "positive" : "negative"}
            />
            <StatCard
              label="Total Accounts"
              value={String(stats.totalAccounts)}
              icon={<Layers className="h-4 w-4" />}
            />
            <StatCard
              label="Online EAs"
              value={String(stats.onlineEAs)}
              icon={<Wifi className="h-4 w-4" />}
              tone="positive"
            />
            <StatCard
              label="Offline EAs"
              value={String(stats.offlineEAs)}
              icon={<WifiOff className="h-4 w-4" />}
              tone={stats.offlineEAs > 0 ? "negative" : "default"}
            />
            <StatCard
              label="Total Open Trades"
              value={String(stats.totalOpenTrades)}
              icon={<ListOrdered className="h-4 w-4" />}
            />
            <StatCard
              label="Total Lots Today"
              value={formatNumber(stats.totalLotsToday)}
              icon={<Scale className="h-4 w-4" />}
            />
            <StatCard
              label="Worst Daily Max Float"
              value={worstFloatEa ? formatCurrency(worstFloatEa.daily_max_float) : "-"}
              subtext={worstFloatEa ? worstFloatEa.account_name || worstFloatEa.ea_name : undefined}
              icon={<TrendingDown className="h-4 w-4" />}
              tone={worstFloatEa && worstFloatEa.daily_max_float < 0 ? "negative" : "default"}
            />
          </>
        )}
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/60">
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <h2 className="text-sm font-semibold text-slate-100">Recently active EAs</h2>
          <Link
            to={groupId !== null ? `/eas?groupId=${groupId}` : "/eas"}
            className="text-sm text-emerald-400 hover:underline"
          >
            View all
          </Link>
        </div>
        <div className="divide-y divide-slate-800/70">
          {easLoading ? (
            <div className="p-5 text-sm text-slate-500">Loading EAs...</div>
          ) : recentEas.length === 0 ? (
            <div className="p-5 text-sm text-slate-500">No EAs have reported in yet.</div>
          ) : (
            recentEas.map((ea) => (
              <Link
                key={ea.id}
                to={`/eas/${ea.id}`}
                className="flex items-center justify-between gap-4 px-5 py-3.5 transition-colors hover:bg-slate-800/40"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-200">
                    {ea.account_name || ea.ea_name}
                  </p>
                  <p className="text-xs text-slate-500">
                    {ea.account_number} ({ea.ea_name}) &middot; {ea.broker} &middot; {ea.symbol}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p
                      className={`text-sm font-medium tabular-nums ${
                        ea.today_profit >= 0 ? "text-emerald-400" : "text-rose-400"
                      }`}
                    >
                      {formatCurrency(ea.today_profit)}
                    </p>
                    <p
                      className={`text-xs tabular-nums ${
                        ea.floating_profit - ea.floating_loss >= 0 ? "text-emerald-400/70" : "text-rose-400/70"
                      }`}
                    >
                      {formatCurrency(ea.floating_profit - ea.floating_loss)} floating
                    </p>
                  </div>
                  <StatusBadge status={ea.status} />
                  <span className="hidden w-20 text-right text-xs text-slate-500 sm:block">
                    {relativeTime(ea.last_heartbeat)}
                  </span>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
